import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { CourseRetentionTerm } from '@/entities/Dashboard';
import { ChartContainer } from '../ChartContainer';
import styles from '../../dashboard/Dashboard.module.scss';

type RetentionBar = {
  level: string;
  retained: number | null;
  phase: 'pending' | 'in_progress' | 'completed' | 'average';
  studiedCount?: number;
  dropoutCount?: number | null;
  isPending: boolean;
};

interface RetentionRateChartProps {
  terms?: CourseRetentionTerm[];
  avgRate?: number | null;
}

const CHART_HEIGHT = 300;

const RetentionTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: Array<{ payload: RetentionBar }>;
}) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  if (item.isPending) return null;

  return (
    <div className="rounded-lg bg-[#1E1E1E] px-4 py-3 text-sm text-white shadow-lg">
      <p className="font-semibold">{item.level}</p>
      <p>{item.retained}% retained</p>
      {item.studiedCount !== undefined ? <p>{item.studiedCount} studied</p> : null}
      {item.dropoutCount ? <p>{item.dropoutCount} dropped</p> : null}
    </div>
  );
};

const barColor = (phase: RetentionBar['phase']) => {
  if (phase === 'in_progress') return '#1E7E34';
  if (phase === 'average') return '#5B3FD1';
  return '#0068D6';
};

const renderRetentionLabel = (props: {
  x?: number;
  y?: number;
  width?: number;
  value?: number | null;
}) => {
  const { x = 0, y = 0, width = 0, value } = props;
  if (value === null || value === undefined) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      textAnchor="middle"
      fill="#0068D6"
      fontSize={14}
      fontWeight={600}
    >
      {value}%
    </text>
  );
};

export const RetentionRateChart = ({ terms = [], avgRate }: RetentionRateChartProps) => {
  const chartData = useMemo<RetentionBar[]>(() => {
    const termBars: RetentionBar[] = terms.map((term) => ({
      level: `Term ${term.number}`,
      retained: term.retention.phase === 'pending' ? null : term.retention.rate ?? 0,
      phase: term.retention.phase,
      studiedCount: term.retention.studiedCount,
      dropoutCount: term.retention.dropoutCount,
      isPending: term.retention.phase === 'pending',
    }));

    termBars.push({
      level: 'Average',
      retained: avgRate ?? null,
      phase: 'average',
      isPending: avgRate === null || avgRate === undefined,
    });

    return termBars;
  }, [terms, avgRate]);

  const counts = useMemo(() => {
    const completed = terms.filter((t) => t.retention.phase === 'completed').length;
    const inProgress = terms.filter((t) => t.retention.phase === 'in_progress').length;
    const pending = terms.filter((t) => t.retention.phase === 'pending').length;
    return { completed, inProgress, pending };
  }, [terms]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-3">
        <span className="rounded-full bg-[#E8F0FF] px-3 py-1 text-xs font-medium text-blue-tertiary">
          {counts.completed} Completed
        </span>
        <span className="rounded-full bg-[#E8F8EE] px-3 py-1 text-xs font-medium text-green-secondary">
          {counts.inProgress} In Progress
        </span>
        <span className="rounded-full bg-[#F0F0F0] px-3 py-1 text-xs font-medium text-grey-50">
          {counts.pending} Pending
        </span>
      </div>

      <ChartContainer height={CHART_HEIGHT}>
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <BarChart data={chartData} margin={{ top: 32, right: 16, left: 8, bottom: 8 }} barCategoryGap="24%">
            <CartesianGrid stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="level"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fill: '#000000', fontSize: 12 }}
              height={48}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8f8f8f', fontSize: 12 }}
              tickFormatter={(v) => `${v}%`}
              domain={[0, 100]}
              ticks={[0, 50, 100]}
            />
            <Tooltip content={<RetentionTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Bar dataKey="retained" maxBarSize={72} radius={[6, 6, 0, 0]}>
              {chartData.map((entry) => (
                <Cell
                  key={entry.level}
                  fill={entry.isPending ? 'transparent' : barColor(entry.phase)}
                  stroke={entry.isPending ? '#D8D8D8' : undefined}
                  strokeDasharray={entry.isPending ? '6 4' : undefined}
                />
              ))}
              <LabelList content={renderRetentionLabel as never} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>

      <div className={styles.chartLegend}>
        <span>
          <span className={styles.legendDot} style={{ background: '#0068D6' }} />
          Completed
        </span>
        <span>
          <span className={styles.legendDot} style={{ background: '#1E7E34' }} />
          In Progress
        </span>
        <span>
          <span className={styles.legendDot} style={{ background: '#5B3FD1' }} />
          Average
        </span>
        <span className="text-grey-50">Pending (TBD)</span>
      </div>
    </div>
  );
};
